import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";

interface ChunkLoadErrorHandlerProps {
  error: Error;
}

export const ChunkLoadErrorHandler = ({ error }: ChunkLoadErrorHandlerProps) => {
  const [countdown, setCountdown] = useState(5);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isChunkError = 
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed") ||
    error.message.includes("Loading chunk") ||
    error.message.includes("ChunkLoadError") ||
    error.message.includes("Unexpected end of script") ||
    error.message.includes("Unexpected token") ||
    error.name === "SyntaxError";

  useEffect(() => {
    if (!isChunkError) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleRefresh();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isChunkError]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  if (!isChunkError) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <RefreshCw className={`h-6 w-6 text-primary ${isRefreshing ? 'animate-spin' : ''}`} />
          </div>
          <CardTitle>Update Available</CardTitle>
          <CardDescription>
            A new version of the app is available. Refreshing to load the latest version.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center text-sm text-muted-foreground">
            {isRefreshing ? (
              <span>Refreshing...</span>
            ) : (
              <span>Auto-refreshing in {countdown} seconds...</span>
            )}
          </div>
          <Button 
            onClick={handleRefresh} 
            className="w-full"
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              "Refresh Now"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const isChunkLoadError = (error: Error): boolean => {
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed") ||
    error.message.includes("Loading chunk") ||
    error.message.includes("ChunkLoadError") ||
    error.message.includes("Unexpected end of script") ||
    error.message.includes("Unexpected token") ||
    error.name === "SyntaxError"
  );
};
